import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Plus } from 'lucide-react'
import {
  api,
  type CreateOutreachCampaignPayload,
  type OutreachCampaign,
  type OutreachCampaignSummary,
} from '@/api/client'
import { Button, ButtonLink, ConfirmDialog, Dialog, Field, FieldGroup, Input, PageHeader, StatTile, StatusPill, Textarea } from '@/ui'
import { ErrorNotice, Hollow, LoadingCard, errorMessage, isAbortError } from './shared'

const blankCampaign: CreateOutreachCampaignPayload = {
  name: '',
  app_name: '',
  app_tagline: '',
  app_description: '',
  app_url: '',
  product_hunt_url: '',
  product_hunt_post_id: '',
  status: 'active',
  timezone: 'Asia/Seoul',
  lookback_days: 30,
  max_upvotes: 50,
  topics: [],
  connection_daily_limit: 10,
  praise_daily_limit: 10,
  cta_daily_limit: 10,
}

/** /outreach: Product Hunt makers worked by hand, one campaign per app. */
export default function Outreach() {
  const [summaries, setSummaries] = useState<OutreachCampaignSummary[]>([])
  const [showForm, setShowForm] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [editing, setEditing] = useState<OutreachCampaign | null>(null)
  const [archiving, setArchiving] = useState<OutreachCampaign | null>(null)
  const [loading, setLoading] = useState(true)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    setSummaries([])
    setShowForm(false)
    void api
      .outreachCampaignSummaries(controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) setSummaries(response.items ?? [])
      })
      .catch((err: unknown) => {
        if (!isAbortError(err)) {
          setSummaries([])
          setError(errorMessage(err))
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [reloadKey])

  const setStatus = async (campaign: OutreachCampaign, status: OutreachCampaign['status']) => {
    setBusyId(campaign.id)
    setError(null)
    try {
      await api.updateOutreachCampaign(campaign.id, { ...campaign, status })
      setReloadKey((value) => value + 1)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <>
      <PageHeader
        title="아웃리치"
        description="Product Hunt 메이커를 찾고 메시지 초안을 만드는 곳이다. LinkedIn 검색, 프로필 방문, 복사, 발송은 모두 사람이 직접 한다."
        actions={
          !error ? (
            <Button variant="primary" icon={Plus} onClick={() => setShowForm(true)}>
              새 캠페인
            </Button>
          ) : undefined
        }
      />
      <div className="space-y-4">
        <ErrorNotice message={error} />
        {!error && (
          <CampaignForm
            open={showForm}
            onClose={() => setShowForm(false)}
            onCreated={() => {
              setShowForm(false)
              setReloadKey((value) => value + 1)
            }}
          />
        )}

        <ConfirmDialog
          open={archiving !== null}
          onOpenChange={(open) => !open && setArchiving(null)}
          title={`${archiving?.name ?? ''} 캠페인을 보관할까`}
          description="보관하면 발굴과 승인, 보냄 표시가 막히고 목록 아래로 내려간다. 보관 해제를 누르면 멈춘 상태로 돌아오고, 재개는 따로 누른다."
          confirmLabel="보관"
          confirmVariant="destructive"
          onConfirm={async () => {
            if (archiving) await setStatus(archiving, 'archived')
            setArchiving(null)
          }}
        />
        {editing && (
          <CampaignEditForm
            campaign={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null)
              setReloadKey((value) => value + 1)
            }}
          />
        )}

        {error ? (
          <div className="flex justify-start">
            <Button onClick={() => setReloadKey((value) => value + 1)}>다시 시도</Button>
          </div>
        ) : loading ? (
          <LoadingCard>캠페인을 불러오는 중…</LoadingCard>
        ) : summaries.length === 0 ? (
          <Hollow>아웃리치 캠페인이 없다. 새 캠페인에서 앱과 Product Hunt 주소를 넣으면 시작한다.</Hollow>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {summaries.map(({ campaign, analytics: summary }) => (
              <section
                key={campaign.id}
                aria-label={campaign.name}
                className="overflow-hidden rounded-lg border border-border bg-card"
              >
                <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-section font-semibold">
                      <Link to={`/outreach/${campaign.id}/discovery`} className="hover:underline">
                        {campaign.name}
                      </Link>
                    </h2>
                    <p className="mt-0.5 text-meta text-muted-foreground">
                      {campaign.app_name} · 최근 {campaign.lookback_days}일 · 추천 {campaign.max_upvotes} 이하
                    </p>
                  </div>
                  <StatusPill domain="app" status={campaign.status} />
                </div>
                <div className="space-y-4 p-4">
                  <div className="grid grid-cols-3 gap-2">
                    <StatTile label="발견" value={summary?.discovered ?? 0} />
                    <StatTile label="연결됨" value={summary?.connected ?? 0} />
                    <StatTile label="답장" value={summary?.replies ?? 0} />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <ButtonLink variant="primary" size="sm" to={`/outreach/${campaign.id}/discovery`}>
                      캠페인 열기
                    </ButtonLink>
                    {campaign.status !== 'archived' && (
                      <Button
                        size="sm"
                        disabled={busyId === campaign.id}
                        onClick={() => void setStatus(campaign, campaign.status === 'active' ? 'paused' : 'active')}
                      >
                        {campaign.status === 'active' ? '멈춤' : '재개'}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setEditing(campaign)}>
                      수정
                    </Button>
                    {campaign.status !== 'archived' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busyId === campaign.id}
                        onClick={() => setArchiving(campaign)}
                      >
                        보관
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        disabled={busyId === campaign.id}
                        onClick={() => void setStatus(campaign, 'paused')}
                      >
                        보관 해제
                      </Button>
                    )}
                  </div>
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

function CampaignForm({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState(blankCampaign)
  const [topics, setTopics] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const field = <K extends keyof CreateOutreachCampaignPayload>(key: K, value: CreateOutreachCampaignPayload[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.createOutreachCampaign({
        ...form,
        topics: topics
          .split(',')
          .map((topic) => topic.trim())
          .filter(Boolean),
      })
      setForm(blankCampaign)
      setTopics('')
      onCreated()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title="새 캠페인"
      description="기본값은 최근 30일, 추천 50 이하, 단계별 하루 10건이다."
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button type="submit" form="outreach-campaign-form" variant="primary" loading={busy}>
            캠페인 만들기
          </Button>
        </>
      }
    >
      <form id="outreach-campaign-form" onSubmit={submit} className="max-h-[65vh] space-y-4 overflow-y-auto pb-2">
        <FieldGroup columns={2}>
          <Field label="캠페인 이름" required>
            <Input required value={form.name} onChange={(e) => field('name', e.target.value)} />
          </Field>
          <Field label="앱 이름" required>
            <Input required value={form.app_name} onChange={(e) => field('app_name', e.target.value)} />
          </Field>
          <Field label="앱 한 줄 소개" required>
            <Input required value={form.app_tagline} onChange={(e) => field('app_tagline', e.target.value)} />
          </Field>
          <Field label="시간대" required>
            <Input required value={form.timezone} onChange={(e) => field('timezone', e.target.value)} />
          </Field>
          <Field label="앱 주소" required>
            <Input
              required
              type="url"
              placeholder="https://example.com"
              value={form.app_url}
              onChange={(e) => field('app_url', e.target.value)}
            />
          </Field>
          <Field label="Product Hunt 주소" required>
            <Input
              required
              type="url"
              placeholder="https://www.producthunt.com/posts/..."
              value={form.product_hunt_url}
              onChange={(e) => field('product_hunt_url', e.target.value)}
            />
          </Field>
          <Field label="Product Hunt 글 ID" hint="비워 두면 성과 화면에서 추천 수를 지금 기록할 수 없다.">
            <Input value={form.product_hunt_post_id} onChange={(e) => field('product_hunt_post_id', e.target.value)} />
          </Field>
          <Field label="주제 (쉼표로 구분)">
            <Input value={topics} onChange={(e) => setTopics(e.target.value)} />
          </Field>
        </FieldGroup>
        <Field label="앱 설명" required>
          <Textarea
            required
            rows={3}
            value={form.app_description}
            onChange={(e) => field('app_description', e.target.value)}
          />
        </Field>
        <FieldGroup columns={2}>
          <NumberField label="조회 기간 (일)" value={form.lookback_days} min={1} onChange={(v) => field('lookback_days', v)} />
          <NumberField label="추천 수 상한" value={form.max_upvotes} min={0} onChange={(v) => field('max_upvotes', v)} />
          <NumberField
            label="연결 요청 / 일"
            value={form.connection_daily_limit}
            min={0}
            onChange={(v) => field('connection_daily_limit', v)}
          />
          <NumberField
            label="칭찬 메시지 / 일"
            value={form.praise_daily_limit}
            min={0}
            onChange={(v) => field('praise_daily_limit', v)}
          />
          <NumberField label="제안 / 일" value={form.cta_daily_limit} min={0} onChange={(v) => field('cta_daily_limit', v)} />
        </FieldGroup>
        <ErrorNotice message={error} />
      </form>
    </Dialog>
  )
}

/** Edits what a running campaign can change: the name, the Product Hunt post,
 *  the topics, the window and the daily limits. The app and its addresses are
 *  not here — they belong to what the messages already say. */
function CampaignEditForm({
  campaign,
  onClose,
  onSaved,
}: {
  campaign: OutreachCampaign
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(campaign.name)
  const [postId, setPostId] = useState(campaign.product_hunt_post_id)
  const [topics, setTopics] = useState((campaign.topics ?? []).join(', '))
  const [lookback, setLookback] = useState(campaign.lookback_days)
  const [maxUpvotes, setMaxUpvotes] = useState(campaign.max_upvotes)
  const [connection, setConnection] = useState(campaign.connection_daily_limit)
  const [praise, setPraise] = useState(campaign.praise_daily_limit)
  const [cta, setCta] = useState(campaign.cta_daily_limit)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.updateOutreachCampaign(campaign.id, {
        ...campaign,
        name: name.trim(),
        product_hunt_post_id: postId.trim(),
        topics: topics
          .split(',')
          .map((topic) => topic.trim())
          .filter(Boolean),
        lookback_days: lookback,
        max_upvotes: maxUpvotes,
        connection_daily_limit: connection,
        praise_daily_limit: praise,
        cta_daily_limit: cta,
      })
      onSaved()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={`${campaign.name} 수정`}
      description="앱 이름과 주소는 바꾸지 않는다. 이미 만든 메시지에 들어가 있다."
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button type="submit" form="outreach-campaign-edit" variant="primary" loading={busy} disabled={!name.trim()}>
            저장
          </Button>
        </>
      }
    >
      <form id="outreach-campaign-edit" onSubmit={submit} className="max-h-[65vh] space-y-4 overflow-y-auto pb-2">
        <FieldGroup columns={2}>
          <Field label="캠페인 이름" required>
            <Input required value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Product Hunt 글 ID" hint="비워 두면 성과 화면에서 추천 수를 지금 기록할 수 없다.">
            <Input value={postId} onChange={(e) => setPostId(e.target.value)} />
          </Field>
        </FieldGroup>
        <Field label="주제 (쉼표로 구분)">
          <Input value={topics} onChange={(e) => setTopics(e.target.value)} />
        </Field>
        <FieldGroup columns={2}>
          <NumberField label="조회 기간 (일)" value={lookback} min={1} onChange={setLookback} />
          <NumberField label="추천 수 상한" value={maxUpvotes} min={0} onChange={setMaxUpvotes} />
          <NumberField label="연결 요청 / 일" value={connection} min={0} onChange={setConnection} />
          <NumberField label="칭찬 메시지 / 일" value={praise} min={0} onChange={setPraise} />
          <NumberField label="제안 / 일" value={cta} min={0} onChange={setCta} />
        </FieldGroup>
        <ErrorNotice message={error} />
      </form>
    </Dialog>
  )
}

function NumberField({
  label,
  value,
  min,
  onChange,
}: {
  label: string
  value: number
  min: number
  onChange: (value: number) => void
}) {
  return (
    <Field label={label}>
      <Input required type="number" min={min} value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </Field>
  )
}
